/**
 * LabelResults — Session 3 rebuild.
 *
 * We read, we do not judge. Monochrome: no colour dots, no grade, no letter.
 * Ordering and typographic weight carry severity, not colour.
 * Every qualitative line is a placement against a NAMED, PUBLISHED threshold
 * (UK FoP / WHO / FSSAI) computed server-side — tap any line for the source.
 */
import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { useAuthStore } from "../../store/authStore";
import { isGuest, trackGuest, useGuestStore } from "../../store/guestStore";
import { fireScanEvent } from "../../utils/compareFacts";

// Warm grey tokens — label results only. brand-gold is reserved for
// FitPoints/Arena and MUST NOT appear here.
const T = {
  base: "#ECEAE6",
  surface: "#E4E2DE",
  inset: "#D8D6D1",
  ink: "#2A2825",
  ink2: "rgba(42,40,37,0.65)",
  ink3: "rgba(42,40,37,0.40)",
  line: "rgba(42,40,37,0.10)",
};
const HEAD = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
const BODY = { fontFamily: "'Barlow', system-ui, sans-serif" };

// INS/E code → common name. Extend over time; never invent.
const INS_NAMES = {
  "100": "Curcumin", "101": "Riboflavin", "150d": "Caramel",
  "160a": "Beta-carotene", "160b": "Annatto",
  "200": "Sorbic acid", "202": "Potassium sorbate",
  "211": "Sodium benzoate", "220": "Sulphur dioxide",
  "223": "Sodium metabisulphite",
  "260": "Acetic acid", "270": "Lactic acid", "296": "Malic acid",
  "300": "Ascorbic acid (Vitamin C)", "306": "Tocopherols (Vitamin E)",
  "319": "TBHQ (antioxidant)", "320": "BHA (antioxidant)", "321": "BHT (antioxidant)",
  "322": "Lecithin (soy or sunflower)",
  "330": "Citric acid", "331": "Sodium citrate", "332": "Potassium citrate",
  "334": "Tartaric acid", "339": "Sodium phosphate",
  "407": "Carrageenan", "410": "Locust bean gum", "412": "Guar gum",
  "415": "Xanthan gum", "440": "Pectin", "466": "Sodium CMC (thickener)",
  "471": "Mono- & diglycerides (emulsifier)",
  "500": "Sodium carbonates (raising agent)",
  "503": "Ammonium carbonates (raising agent)",
  "621": "MSG (flavour enhancer)", "627": "Disodium guanylate",
  "631": "Disodium inosinate", "635": "Ribonucleotides (flavour enhancer)",
  "950": "Acesulfame K (sweetener)", "951": "Aspartame (sweetener)",
  "955": "Sucralose (sweetener)",
};

const TABLE_ROWS = [
  ["energy_kcal", "Energy", "kcal"],
  ["protein_g", "Protein", "g"],
  ["carbohydrate_g", "Carbohydrate", "g"],
  ["sugars_g", "of which sugars", "g"],
  ["fibre_g", "Fibre", "g"],
  ["fat_g", "Fat", "g"],
  ["saturated_fat_g", "of which saturates", "g"],
  ["trans_fat_g", "Trans fat", "g"],
  ["sodium_mg", "Sodium", "mg"],
  ["cholesterol_mg", "Cholesterol", "mg"],
];

const fmtVal = (v, unit) => {
  if (v == null) return "—";
  const n = unit === "mg" || unit === "kcal" ? Math.floor(v + 0.5) : Math.floor(v * 10 + 0.5) / 10;
  return `${n}${unit === "kcal" ? "" : unit}`;
};
const sugarTsp = (g) => (g == null ? null : Math.floor((g / 4) * 10 + 0.5) / 10);

export default function LabelResults({ label, scannedImage, onDiscard, onSaved, onAddPhoto, onCompare }) {
  const user = useAuthStore((s) => s.user);
  const guest = !user && isGuest();
  const guestStore = useGuestStore();
  const c = label?.canonical || {};
  const [sheet, setSheet] = useState(null); // insight detail
  const [portionOpen, setPortionOpen] = useState(false);
  const [customGrams, setCustomGrams] = useState("");
  const [logging, setLogging] = useState(false);

  const ingredientsOnly = c.result_state === "ingredients_only";
  const insights = c.insights || [];
  const packet = c.packet_summary || null;
  const per100 = c.nutrition_per_100;
  const perServing = c.nutrition_per_serving;
  const tableBlock = per100 || perServing;
  const tableBasis = per100
    ? (per100.basis === "per_100ml" ? "Per 100ml" : "Per 100g")
    : "Per serving";
  const hasNullField = useMemo(
    () => !!tableBlock && TABLE_ROWS.some(([k]) => tableBlock[k] == null),
    [tableBlock]
  );
  const ingredients = Array.isArray(c.ingredients) ? c.ingredients : [];
  const hasIngredients = ingredients.length > 0;
  const additives = c.additives || [];
  const tier = (label?.confidence || "").toLowerCase();
  const validationNote = c.validation?.note;

  const product = c.product || {};
  const serving = c.serving || {};
  const smallPack = serving.package_size_value != null && serving.package_size_value < 100;

  const doLog = async (mode, grams) => {
    fireScanEvent("log_pressed", { mode });
    if (!user) {
      trackGuest("label_save_gate_shown", {});
      toast.error("Sign up to save this to your day");
      return;
    }
    setLogging(true);
    try {
      const body = { canonical: c, mode };
      if (mode === "custom") body.grams = Number(grams);
      const { data } = await apiClient.post("/nutrition/log-label", body);
      toast.success(`Logged · ${data?.logged?.calories ?? ""} kcal`);
      setPortionOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally {
      setLogging(false);
    }
  };

  // Session 4B — save for re-log/compare. Guests keep it in localStorage
  // until signup migrates it.
  const [saving, setSaving] = useState(false);
  const doSave = async () => {
    fireScanEvent("product_saved", {});
    if (!user) {
      guestStore.addSavedProduct(c);
      toast.success("Saved on this device — sign up to keep it");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/nutrition/saved", { canonical: c });
      toast.success("Saved to your products");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const btnPrimary = {
    ...BODY, height: 52, borderRadius: 100, background: T.ink, color: T.base,
    fontWeight: 600, fontSize: 15, width: "100%",
  };
  const btnSecondary = {
    ...BODY, height: 44, borderRadius: 100, background: "transparent",
    color: T.ink, border: `1px solid ${T.line}`, fontWeight: 600, fontSize: 14, width: "100%",
  };

  return (
    <div
      data-testid="label-results"
      style={{ ...BODY, background: T.base, color: T.ink, borderRadius: 16, border: `1px solid ${T.line}`, padding: 20, paddingBottom: 8 }}
    >
      {/* 1 · PRODUCT — omit silently when the name was not read */}
      {(product.name || product.brand) && (
        <div data-testid="label-product">
          <div style={{ ...HEAD, fontSize: 26, fontWeight: 700, lineHeight: 1.05 }}>
            {product.name || product.brand}
          </div>
          <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>
            {[product.brand && product.name ? product.brand : null,
              product.category ? `${product.category}` : null,
              "read from label"].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}

      {/* 2 · INSIGHTS — ranked, first line heavier. Tap for the threshold. */}
      {insights.length > 0 && (
        <div data-testid="label-insights" style={{ marginTop: 18 }}>
          {insights.map((ins, i) => (
            <button
              key={ins.text}
              type="button"
              data-testid={`insight-line-${i}`}
              onClick={() => {
                setSheet(ins);
                fireScanEvent("insight_tapped", { nutrient: ins.nutrient });
              }}
              style={{
                ...HEAD, display: "block", width: "100%", textAlign: "left",
                fontSize: i === 0 ? 22 : 19, fontWeight: i === 0 ? 800 : 600,
                color: T.ink, padding: "6px 0", background: "transparent",
                borderBottom: `1px solid ${T.line}`,
              }}
            >
              {ins.text}
            </button>
          ))}
          <div style={{ fontSize: 11, color: T.ink3, marginTop: 6 }}>
            Tap any line for the threshold used
          </div>
        </div>
      )}

      {/* 3 · YOUR PACKET — omitted entirely when package size wasn't read */}
      {!ingredientsOnly && packet && (
        <div
          data-testid="packet-block"
          style={{ marginTop: 18, background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: 16 }}
        >
          <div style={{ ...HEAD, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", color: T.ink2 }}>
            {packet.heading}
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "6px 18px", alignItems: "baseline" }}>
            {packet.energy_kcal != null && (
              <span style={{ ...HEAD, fontSize: 28, fontWeight: 800 }} data-testid="packet-energy">
                {packet.energy_kcal} kcal
              </span>
            )}
            {(packet.highlights || []).map((h) => (
              <span key={h.nutrient} style={{ fontSize: 14, color: T.ink2 }} data-testid={`packet-h-${h.nutrient}`}>
                <b style={{ color: T.ink, fontWeight: 600 }}>{h.value}{h.unit} {h.label}</b>
                {h.pct_day != null ? ` · ${h.pct_day}% of a day` : ""}
              </span>
            ))}
          </div>
          {packet.sentence && (
            <div style={{ marginTop: 8, fontSize: 14, color: T.ink }} data-testid="packet-sentence">
              {packet.sentence}
            </div>
          )}
          {packet.serving_line && (
            <div style={{ marginTop: 4, fontSize: 12, color: T.ink3 }} data-testid="packet-serving-line">
              {packet.serving_line}
            </div>
          )}
        </div>
      )}

      {/* 4 · TABLE — null shows —, never 0 */}
      {!ingredientsOnly && tableBlock && (
        <div data-testid="per100-table" style={{ marginTop: 18 }}>
          <div style={{ ...HEAD, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", color: T.ink2 }}>
            {tableBasis.toUpperCase()}
          </div>
          <table style={{ width: "100%", marginTop: 6, borderCollapse: "collapse", fontSize: 14 }}>
            <tbody>
              {TABLE_ROWS.map(([k, name, unit]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${T.line}` }} data-testid={`row-${k}`}>
                  <td style={{ padding: "7px 0", color: T.ink2 }}>{name}</td>
                  <td style={{ padding: "7px 0", textAlign: "right", fontWeight: 600 }}>
                    {k === "energy_kcal"
                      ? (tableBlock[k] == null ? "—" : `${fmtVal(tableBlock[k], "kcal")} kcal`)
                      : fmtVal(tableBlock[k], unit)}
                    {k === "sugars_g" && tableBlock[k] != null && (
                      <span style={{ color: T.ink3, fontWeight: 400 }}> · ≈{sugarTsp(tableBlock[k])} tsp</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasNullField && (
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 6 }} data-testid="null-footnote">
              Fields shown as — were not stated on the label.
            </div>
          )}
        </div>
      )}

      {/* 5 · INGREDIENTS — exactly as printed, in order */}
      {hasIngredients && (
        <div data-testid="label-ingredients" style={{ marginTop: 18 }}>
          <div style={{ ...HEAD, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", color: T.ink2 }}>
            INGREDIENTS
          </div>
          <p style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, color: T.ink }}>
            {ingredients.join(", ")}
            {label?.ingredients_truncated ? " …" : ""}
          </p>
          <div style={{ fontSize: 11, color: T.ink3, marginTop: 4 }}>
            Ingredients are listed in descending order by weight.
          </div>
          {additives.length > 0 && (
            <div style={{ marginTop: 8 }} data-testid="label-additives">
              {additives.map((a) => {
                const code = String(a.code || "").replace(/^(INS|E)\s*/i, "");
                const name = a.name || INS_NAMES[code.toLowerCase()] || INS_NAMES[code];
                return (
                  <div key={a.code} style={{ fontSize: 12, color: T.ink2, padding: "2px 0" }}>
                    {a.code}{name ? ` — ${name}` : ""}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Session 2C — result-state CTAs. Cost stated BEFORE the second photo. */}
      {ingredientsOnly && onAddPhoto && (
        <div data-testid="label-add-panel-cta" style={{ marginTop: 18, background: T.surface, borderRadius: 12, border: `1px solid ${T.line}`, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>We read the ingredients — no nutrition numbers yet.</div>
          <p style={{ fontSize: 12, color: T.ink2, marginTop: 4 }}>
            Add a photo of the nutrition panel to complete this product. Uses 1 scan.
          </p>
          <button type="button" data-testid="label-add-panel" onClick={() => onAddPhoto("panel")}
                  style={{ ...btnSecondary, marginTop: 10 }}>
            Add the nutrition panel
          </button>
        </div>
      )}
      {!ingredientsOnly && !hasIngredients && onAddPhoto && (
        <div data-testid="label-add-ingredients-cta" style={{ marginTop: 18 }}>
          <button type="button" data-testid="label-add-ingredients" onClick={() => onAddPhoto("ingredients")}
                  style={btnSecondary}>
            Add the ingredients · uses 1 scan
          </button>
        </div>
      )}

      {/* 6 · CONFIDENCE + VALIDATION, plain words */}
      <div data-testid="label-confidence" style={{ marginTop: 16, fontSize: 12, color: T.ink2 }}>
        {tier === "high" ? "Label read clearly." : "Some values were hard to read."}
        {validationNote ? ` ${validationNote}` : ""}
      </div>

      {/* 7 · ACTIONS */}
      <div style={{ marginTop: 14, paddingBottom: 12 }}>
        {!ingredientsOnly && (
          <button type="button" data-testid="label-log-btn" style={btnPrimary}
                  onClick={() => (user ? setPortionOpen(true) : doLog("serving"))}>
            Log this
          </button>
        )}
        {!ingredientsOnly && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" data-testid="label-save-btn" disabled={saving}
                    onClick={doSave} style={{ ...btnSecondary, flex: 1 }}>
              Save
            </button>
            {onCompare && per100 && (
              <button type="button" data-testid="label-compare-btn"
                      onClick={() => {
                        fireScanEvent("compare_started", { from: "scan" });
                        onCompare();
                      }}
                      style={{ ...btnSecondary, flex: 1 }}>
                Compare with another
              </button>
            )}
          </div>
        )}
        <button type="button" data-testid="label-scan-another" onClick={onDiscard}
                style={{ ...btnSecondary, marginTop: 8 }}>
          Scan another
        </button>
      </div>

      {/* Threshold sheet */}
      {sheet && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(42,40,37,0.45)" }}
             onClick={() => setSheet(null)} data-testid="insight-sheet">
          <div onClick={(e) => e.stopPropagation()}
               style={{ ...BODY, position: "absolute", left: 0, right: 0, bottom: 0, maxWidth: 672, margin: "0 auto",
                        background: T.base, color: T.ink, borderRadius: "16px 16px 0 0",
                        border: `1px solid ${T.line}`, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ ...HEAD, fontSize: 22, fontWeight: 800 }}>{sheet.text}</div>
              <button type="button" onClick={() => setSheet(null)} data-testid="insight-sheet-close"
                      style={{ background: "transparent", color: T.ink2 }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: 14 }} data-testid="sheet-definition">{sheet.detail?.definition}</div>
            <div style={{ marginTop: 6, fontSize: 14, fontWeight: 600 }} data-testid="sheet-product">{sheet.detail?.product_line}</div>
            <div style={{ marginTop: 10, fontSize: 12, color: T.ink2 }} data-testid="sheet-threshold">{sheet.detail?.threshold_line}</div>
            {sheet.detail?.source_note && (
              <div style={{ marginTop: 4, fontSize: 11, color: T.ink3 }}>{sheet.detail.source_note}</div>
            )}
          </div>
        </div>
      )}

      {/* Portion sheet — what did you eat? Values computed server-side. */}
      {portionOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(42,40,37,0.45)" }}
             onClick={() => setPortionOpen(false)} data-testid="portion-sheet">
          <div onClick={(e) => e.stopPropagation()}
               style={{ ...BODY, position: "absolute", left: 0, right: 0, bottom: 0, maxWidth: 672, margin: "0 auto",
                        background: T.base, color: T.ink, borderRadius: "16px 16px 0 0",
                        border: `1px solid ${T.line}`, padding: 20 }}>
            <div style={{ ...HEAD, fontSize: 22, fontWeight: 800 }}>How much did you have?</div>
            <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
              {serving.package_size_value != null && (
                <button type="button" data-testid="portion-whole" disabled={logging}
                        onClick={() => doLog("whole_pack")}
                        style={smallPack ? btnPrimary : btnSecondary}>
                  The whole pack · {serving.package_size_value}{serving.package_size_unit || "g"}
                </button>
              )}
              {serving.size_value != null && (
                <button type="button" data-testid="portion-serving" disabled={logging}
                        onClick={() => doLog("serving")}
                        style={smallPack || serving.package_size_value == null ? btnSecondary : btnPrimary}>
                  One serving · {serving.size_value}{serving.size_unit || "g"}
                </button>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="number" inputMode="numeric" placeholder="Custom grams"
                  value={customGrams} onChange={(e) => setCustomGrams(e.target.value)}
                  data-testid="portion-custom-input"
                  style={{ ...BODY, flex: 1, height: 44, borderRadius: 12, border: `1px solid ${T.line}`,
                           background: T.surface, color: T.ink, padding: "0 12px", fontSize: 14, outline: "none" }}
                />
                <button type="button" data-testid="portion-confirm"
                        disabled={logging || !Number(customGrams)}
                        onClick={() => doLog("custom", customGrams)}
                        style={{ ...btnSecondary, width: 96, opacity: Number(customGrams) ? 1 : 0.5 }}>
                  Log
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
